import { createI18n } from 'vue-i18n'
import { Ref } from 'vue'
import { refValue } from '@/helper/tools'

const i18n = createI18n({
  legacy: true,
  fallbackLocale: 'en',
  warnHtmlInMessage: 'off',
  messages: {
    en: {
      global: {
        project_name: 'System configurator',
        currency: 'EUR',
        login_fail_title: 'You\'ve been logged out',
        login_fail_text: 'Your session has expired. Please return to the Vaillant web page and log in again.'
      }
    },
    nl: {
      global: {
        project_name: 'Systeemconfigurator',
        currency: 'EUR',
        login_fail_title: 'U bent uitgelogd',
        login_fail_text:
          'Uw sessie is verlopen. Ga terug naar de Vaillant webpagina en ' +
          '<a href="https://www.vaillant.nl/professioneel/services/apps-tools/warmtepomp-planning-tool/"' +
          '>meld u hier opnieuw aan</a>.'
      }
    },
    es: {
      global: {
        project_name: 'proTOOL',
        currency: 'EUR',
        login_fail_title: 'Se ha cerrado la sesión',
        login_fail_text:
          'Su sesión ha expirado. Por favor, vuelva a la página web de Vaillant e inicie sesión de nuevo.'
      }
    },
    pl: {
      global: {
        project_name: 'Konfigurator systemu',
        currency: 'EUR',
        login_fail_title: 'Zostałeś wylogowany',
        login_fail_text: 'Twoja sesja wygasła. Proszę wrócić na stronę internetową Vaillant i zalogować się ponownie.'
      }
    }
  }
})

i18n.global.silentTranslationWarn = true
i18n.global.silentFallbackWarn = true
export default i18n

export function formatLanguage(): string {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return i18n.global.locale == 'xx' ? 'en' : i18n.global.locale
}

export function formatPrice(price: number | undefined, currentIsoLocale: string): string {
  if (price === undefined) return ''

  const formatPrice = formatDisplayCurrency(currentIsoLocale, price)
  if (formatPrice) {
    return formatPrice
  }

  const currencyCode = i18n.global.t('global.currency').toString()

  try {
    return (
      new Intl.NumberFormat(formatLanguage(), {
        style: 'currency',
        currency: currencyCode
      }).format(price)
    )
  } catch (e) {
    return `${new Intl.NumberFormat(formatLanguage(), {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price)} ${currencyCode}!`
  }
}

export function getLocaleFractionDivider() {
  return (1.1).toLocaleString(formatLanguage())[1]
}

export function formatNumber(value: number | undefined, maxDigits = 2, withThousandDividers = true): string {
  const result = value !== undefined ? value.toLocaleString(formatLanguage(), { maximumFractionDigits: maxDigits }) : ''
  return withThousandDividers
    ? result
    : result.replace(new RegExp('[^-\\d' + getLocaleFractionDivider() + ']', 'g'), '')
}

export function parseFloatLocale(input: string | Ref<string>): number {
  return parseFloat(
    refValue(input)
      .toString()
      .replace(new RegExp('[^-\\d' + getLocaleFractionDivider() + ']', 'g'), '')
      .replace(',', '.')
  )
}

export function isTranslationPresent(input: string): boolean {
  if (!i18n.global.te(input)) {
    return false
  }
  const value = i18n.global.t(input)
  return value != undefined && !value.startsWith('~')
}

/**
 * Format currency with presets which matches
 * with pdf report currencies
 *
 * @param isoLocale
 * @param price
 */
export function formatDisplayCurrency(isoLocale: string, price: number): string | null {
  const currencies = [
    { isoLocale: 'hr-HR', symbol: '€', isoCode: 'EUR', displayOrder: 'end' },
    { isoLocale: 'bs-BA', symbol: 'KM', isoCode: 'BAM', displayOrder: 'end' },
    { isoLocale: 'nl-BE', symbol: '€', isoCode: 'EUR', displayOrder: 'end' },
    { isoLocale: 'sr-RS', symbol: 'дин', isoCode: 'RSD', displayOrder: 'end' }
  ]

  const currencyObj = currencies.find(currency => currency.isoLocale === isoLocale)
  if (!currencyObj) return null

  const formatPrice = new Intl.NumberFormat(formatLanguage(), {
    style: 'currency',
    currency: currencyObj.isoCode
  }).format(price)

  // Remove all non-digit characters and keep ',.'
  const formatCurrencyPrice = formatPrice.replace(/[^0-9.,]/g, '')

  return currencyObj.displayOrder === 'start'
    ? `${currencyObj.symbol} ${formatCurrencyPrice}`
    : `${formatCurrencyPrice} ${currencyObj.symbol}`
}
